import {
  createDefaultClassLessonAlarmCustomSchedule,
  normalizeClassLessonAlarmCustomSchedule,
  type ClassLessonAlarmCustomSchedule,
} from './classLessonAlarmCustomSchedule';

const CACHE_VERSION = 1 as const;
const CACHE_KEY_PREFIX = 'cp:class-lesson-alarm-custom-schedule:v1:';

type Cached = {
  version: typeof CACHE_VERSION;
  workspaceId: string;
  schedule: ClassLessonAlarmCustomSchedule;
  savedAt: string;
};

function cacheKey(workspaceId: string): string {
  return `${CACHE_KEY_PREFIX}${workspaceId.trim()}`;
}

export function loadClassLessonAlarmCustomScheduleCache(
  workspaceId: string
): { found: false } | { found: true; schedule: ClassLessonAlarmCustomSchedule } {
  const ws = workspaceId.trim();
  if (!ws || typeof window === 'undefined') return { found: false };
  try {
    const raw = localStorage.getItem(cacheKey(ws));
    if (!raw) return { found: false };
    const parsed = JSON.parse(raw) as Cached;
    if (parsed?.version !== CACHE_VERSION || parsed.workspaceId !== ws) return { found: false };
    return {
      found: true,
      schedule: normalizeClassLessonAlarmCustomSchedule(parsed.schedule),
    };
  } catch {
    return { found: false };
  }
}

export function saveClassLessonAlarmCustomScheduleCache(
  workspaceId: string,
  schedule: ClassLessonAlarmCustomSchedule
): void {
  const ws = workspaceId.trim();
  if (!ws || typeof window === 'undefined') return;
  const payload: Cached = {
    version: CACHE_VERSION,
    workspaceId: ws,
    schedule: normalizeClassLessonAlarmCustomSchedule(schedule),
    savedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(cacheKey(ws), JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function clearClassLessonAlarmCustomScheduleCache(workspaceId: string): void {
  const ws = workspaceId.trim();
  if (!ws || typeof window === 'undefined') return;
  try {
    localStorage.removeItem(cacheKey(ws));
  } catch {
    /* ignore */
  }
}

export function peekClassLessonAlarmCustomSchedule(
  workspaceId: string
): ClassLessonAlarmCustomSchedule {
  const cached = loadClassLessonAlarmCustomScheduleCache(workspaceId);
  return cached.found ? cached.schedule : createDefaultClassLessonAlarmCustomSchedule();
}
